import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen pt-20 bg-gray-50 dark:bg-[#0a0e17] transition-colors duration-300">
            {/* Header */}
            <div className="bg-white dark:bg-[#111621] border-b border-gray-100 dark:border-gray-800 py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/" className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 font-medium mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                    </Link>
                    <div className="flex items-center space-x-3 mb-4">
                        <Shield className="w-8 h-8 text-primary-500" />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                        Last Updated: December 7, 2025
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="prose prose-lg dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 space-y-8">

                    <section>
                        <h3>1. Introduction</h3>
                        <p>
                            IronGate Software Limited ("we," "us," or "our") is committed to protecting your personal data.
                            This Privacy Policy specifically addresses our obligations under the <b>General Data Protection Regulation (GDPR)</b> and the <b>Data Protection Act 2018 (Ireland)</b>.
                        </p>
                        <p>
                            By accessing our website (irongate.ie) or engaging our services, you consent to the data practices described in this statement.
                        </p>
                    </section>

                    <section>
                        <h3>2. Data Controller</h3>
                        <p>For the purposes of the GDPR, the Data Controller is:</p>
                        <p className="bg-white dark:bg-white/5 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                            <b>IronGate Software LTD</b><br />
                            Dublin City, Ireland<br />
                            Email: contact@irongate.ie
                        </p>
                    </section>

                    <section>
                        <h3>3. Information We Collect</h3>
                        <p>We perform B2B (Business-to-Business) services. The personal data we collect is minimal and strictly for professional communication:</p>
                        <ul>
                            <li><b>Identity Data:</b> First name, last name, job title.</li>
                            <li><b>Contact Data:</b> Email address, telephone number, company name.</li>
                            <li><b>Technical Data:</b> IP address, browser type, and operating system (collected via standard server logs for security purposes).</li>
                            <li><b>Communication Data:</b> Any information you provide in the contact forms or direct correspondence.</li>
                        </ul>
                    </section>

                    <section>
                        <h3>4. Legal Basis for Processing</h3>
                        <p>We process your personal data under the following legal bases pursuant to Article 6 of the GDPR:</p>
                        <ul>
                            <li><b>Contractual Necessity:</b> To fulfill our obligations if you become a client.</li>
                            <li><b>Legitimate Interests:</b> To respond to your inquiries, improve our services, and ensure network security.</li>
                            <li><b>Consent:</b> Where you have explicitly opted in (e.g., subscribing to a newsletter, if applicable).</li>
                        </ul>
                    </section>

                    <section>
                        <h3>5. Data Retention</h3>
                        <p>
                            We will only retain your personal data for as long as necessary to fulfil the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.
                            In general, client correspondence is retained for 7 years to comply with Irish Revenue Commissioners' record-keeping requirements.
                        </p>
                    </section>

                    <section>
                        <h3>6. International Data Transfers</h3>
                        <p>
                            As an Irish company, our primary processing occurs within the <b>European Economic Area (EEA)</b>.
                            If we engage service providers (processors) outside the EEA (e.g., US-based cloud hosting), we ensure appropriate safeguards are in place, such as EU Standard Contractual Clauses (SCCs) or adequacy decisions.
                        </p>
                    </section>

                    <section>
                        <h3>7. Your Rights Under GDPR</h3>
                        <p>As a data subject, you have the following rights:</p>
                        <ul>
                            <li><b>Right of Access:</b> You can request a copy of the personal data we hold about you.</li>
                            <li><b>Right to Rectification:</b> You can ask us to correct inaccurate data.</li>
                            <li><b>Right to Erasure ("Right to be Forgotten"):</b> You can ask us to delete your data where there is no good reason for us to continue processing it.</li>
                            <li><b>Right to Restriction:</b> You can ask us to suspend processing of your data.</li>
                            <li><b>Right to Object:</b> You can object to processing where we rely on legitimate interest.</li>
                        </ul>
                        <p>
                            To exercise any of these rights, please contact us at <b>contact@irongate.ie</b>. We will respond within one month as mandated by law.
                        </p>
                    </section>

                    <section>
                        <h3>8. Security</h3>
                        <p>
                            We have implemented robust technical and organisational measures to secure your personal data from accidental loss, unauthorised access, alteration, or disclosure.
                            This includes encryption in transit (TLS) and strict access controls within our organisation.
                        </p>
                    </section>

                    <section>
                        <h3>9. Complaints</h3>
                        <p>
                            If you believe we have not processed your data in accordance with the law, you have the right to lodge a complaint with the Irish supervisory authority:
                        </p>
                        <p>
                            <b>Data Protection Commission (DPC)</b><br />
                            21 Fitzwilliam Square South, Dublin 2, D02 RD28.<br />
                            Website: <a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer">www.dataprotection.ie</a>
                        </p>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
